// Left pane: terms grouped by the section that contains their source_block.
// OpenAPI / user-origin terms with no source_block fall into an "Unscoped"
// bucket at the top.

import { useMemo, useState } from "react";
import type { DocJSON, GlossaryEntry } from "../data/types";
import { useReviewStore } from "./store";
import { ReviewRow } from "./ReviewRow";
import { BulkActions } from "./BulkActions";
import { AddTermForm } from "./AddTermForm";

interface Props {
  doc: DocJSON;
}

interface Group {
  sectionId: string;
  heading: string;
  entries: GlossaryEntry[];
}

const UNSCOPED_ID = "__unscoped";
const UNSCOPED_HEADING = "Canonical & user-added";

function groupTerms(doc: DocJSON, proposed: GlossaryEntry[], added: GlossaryEntry[]): Group[] {
  const blockToSection = new Map<string, { id: string; heading: string }>();
  for (const s of doc.sections) {
    const meta = { id: s.id, heading: s.heading ?? s.id };
    for (const b of s.blocks) blockToSection.set(b.id, meta);
  }
  const groups = new Map<string, Group>();
  const ensure = (id: string, heading: string): Group => {
    let g = groups.get(id);
    if (!g) {
      g = { sectionId: id, heading, entries: [] };
      groups.set(id, g);
    }
    return g;
  };
  for (const e of [...proposed, ...added]) {
    const sec = e.source_block_id ? blockToSection.get(e.source_block_id) : undefined;
    const g = sec ? ensure(sec.id, sec.heading) : ensure(UNSCOPED_ID, UNSCOPED_HEADING);
    g.entries.push(e);
  }
  // Preserve doc section order; put UNSCOPED first.
  const ordered: Group[] = [];
  const unscoped = groups.get(UNSCOPED_ID);
  if (unscoped) ordered.push(unscoped);
  for (const s of doc.sections) {
    const g = groups.get(s.id);
    if (g) ordered.push(g);
  }
  return ordered;
}

export function ReviewList({ doc }: Props) {
  const proposed = useReviewStore((s) => s.proposed);
  const added = useReviewStore((s) => s.added);
  const actions = useReviewStore((s) => s.actions);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const groups = useMemo(() => groupTerms(doc, proposed, added), [doc, proposed, added]);

  return (
    <div className="maisight-review-list" role="list" aria-label="Review list">
      {groups.map((g) => {
        const isOpen = !collapsed[g.sectionId];
        const counts = countsFor(g, actions);
        return (
          <section
            key={g.sectionId}
            className="maisight-review-group"
            data-section-id={g.sectionId}
            role="listitem"
          >
            <header className="maisight-review-group-head">
              <button
                type="button"
                className="maisight-review-group-toggle"
                onClick={() => setCollapsed((c) => ({ ...c, [g.sectionId]: isOpen }))}
                aria-expanded={isOpen}
                aria-controls={`group-${g.sectionId}`}
              >
                <span className="maisight-review-group-caret">{isOpen ? "▼" : "▶"}</span>
                <span className="maisight-review-group-heading">{g.heading}</span>
                <span className="maisight-review-group-counts">
                  {counts.total} terms · {counts.pending} pending
                </span>
              </button>
              {isOpen ? <BulkActions sectionId={g.sectionId} entries={g.entries} /> : null}
            </header>
            {isOpen ? (
              <div id={`group-${g.sectionId}`} className="maisight-review-group-body">
                {g.entries.map((e) => (
                  <ReviewRow key={e.term_id} doc={doc} entry={e} />
                ))}
                <AddTermForm sectionId={g.sectionId === UNSCOPED_ID ? null : g.sectionId} />
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}

function countsFor(g: Group, actions: Record<string, { action: string }>) {
  let pending = 0;
  for (const e of g.entries) {
    const a = actions[e.term_id]?.action ?? "pending";
    if (a === "pending") pending++;
  }
  return { total: g.entries.length, pending };
}
